import { FC, ReactElement } from 'react';
import { useParams } from 'react-router';
import first from 'lodash/first';
import {
  IAirSearchResponse,
  ITraveler,
  ITripPnr,
  ListBookingPaymentSourcesRequestType,
  ListBookingPaymentSourcesResponse,
  V2TripDetailsResponseManager,
  useListBookingPaymentSourcesQuery,
  useProfileReadQuery,
} from 'obt-common';
import { GetTripDetailsResponse } from 'obt-common/types/api/v1/obt/trip/trip_details';
import Loader from 'src/app/flights/ExchangeFlightCheckoutPage/shared/Loader';
import PreloadErrorModal from 'src/v2-components/trips/shared/PreloadErrorModal';
import { BookingWorkflowType } from 'obt-common/types/api/v2/obt/model/booking-workflow-type';
import { useGetAirSearchResultsFeatures } from 'obt-common/hooks/Flights/useGetAirSearchResultsFeatures';
import { IOBTShopFeaturesConfig } from 'obt-common/hooks/useOBTShopFeatures';
import useTripDetails from '../../../../hooks/useTripDetails';

export interface Props {
  children: (props: {
    airSelectedItineraryResponse: IAirSearchResponse;
    tripDetailsResponse: GetTripDetailsResponse;
    traveler: ITraveler;
    pnr: ITripPnr;
    paymentSources: ListBookingPaymentSourcesResponse;
    airSearchResultsFeatures: IOBTShopFeaturesConfig;
  }) => ReactElement;
}

/**
 * Loads the required APIs for Retry Payment page and passes responses as props
 * to its children
 */
const PreloadContainer: FC<Props> = ({ children }) => {
  // Leg index might be used for completing the booking
  const { tripId, pnrId } = useParams<{
    tripId: string;
    pnrId: string;
    legIndex: string;
  }>();
  const tripDetails = useTripDetails(tripId);

  // eslint-disable-next-line no-restricted-syntax
  const tripManager = tripDetails.data ? new V2TripDetailsResponseManager(tripDetails.data) : null;
  const pnr = tripManager?.getPnrFromPnrId(pnrId);
  const airSelectedItinerary = pnr?.air?.air;

  const travelerUserOrgId = first(pnr?.air?.travelerInfo)?.traveler?.userOrgId;
  const travelerId = travelerUserOrgId;
  const { data: userProfile } = useProfileReadQuery(travelerId);

  const paymentSources = useListBookingPaymentSourcesQuery(
    {
      type: ListBookingPaymentSourcesRequestType.AIR,
      itineraryId: '',
      pnrId,
      searchId: '',
      userId: userProfile?.userOrgId.userId?.id ?? '',
      initiateBookingResponseId: undefined,
      bookingWorkflowType: BookingWorkflowType.RetryPayment,
      eventId: tripDetails.data?.eventSummary?.id,
      tripId,
    },
    {
      enabled: !!userProfile?.userOrgId.userId?.id,
    },
  );

  const { airSearchResultsFeatures } = useGetAirSearchResultsFeatures(userProfile?.userOrgId?.userId?.id ?? '');

  // Creating an array to anticipate any additional API calls required for this page
  const allQueries = [tripDetails, paymentSources];
  const firstQueryWithError = allQueries.find((query) => query.error);

  if (firstQueryWithError?.error) {
    return <PreloadErrorModal error={firstQueryWithError.error} refetch={firstQueryWithError.refetch} />;
  }

  if (
    tripDetails.data &&
    airSelectedItinerary &&
    userProfile &&
    pnr &&
    paymentSources.data &&
    airSearchResultsFeatures
  ) {
    return children({
      airSelectedItineraryResponse: airSelectedItinerary,
      tripDetailsResponse: tripDetails.data,
      traveler: userProfile,
      pnr,
      paymentSources: paymentSources.data,
      airSearchResultsFeatures,
    });
  }
  return <Loader />;
};

export default PreloadContainer;
